import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle, Building2, Image as ImageIcon, RefreshCw } from "lucide-react";
import useAuth from "../../../hooks/useAuth";
import { getUser } from "../../../api/user";
import { uploadCompanyLogo } from "../../../api/company";
import { Skeleton } from "../../../components/ui/Skeleton";
import { PageHeader } from "../../../components/ui/PageHeader";
import { EmptyState } from "../../../components/ui/EmptyState";
import { Button } from "../../../components/ui/Button";
import { SectionCard } from "../../../components/ui/SectionCard";
import { showToast } from "../../../utils/showToast";
import CompanyInfoCard from "../../../components/companyInfoCard";
import DeliveryFailedFormConfig from "../../../components/deliveryConfig/DeliveryFailedFormConfig";
import LogoUploader from "../../../components/logoUploader";

// ─────────────────────────────────────────────────────────────────────────────
// CompanySettings — Vista de comercio para ver los datos de su empresa.
// Los datos identitarios (razón social, RUT, direcciones) son de solo lectura:
// los edita el admin de la cuenta logística en /admin/settings.
//
// Dos excepciones que el comercio SÍ gestiona acá:
//   · su logo (imagen + versión ZPL), que es identidad propia y no un dato
//     administrativo de la cuenta;
//   · los comercios con flota propia (hasOwnFleet), su paso de entrega fallida.
// ─────────────────────────────────────────────────────────────────────────────

export default function CompanySettings() {
  const { t } = useTranslation();
  const { user: authUser, isLoading: authLoading } = useAuth();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  // Bump para reintentar el fetch sin depender de que cambie authUser.
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    // Mientras el AuthProvider resuelve (p.ej. refresh de token en un reload)
    // authUser todavía es null: NO es un error, se mantiene el skeleton.
    if (authLoading) return;
    if (!authUser?.id) {
      // Auth resuelto y sin id: no hay fetch posible. Antes el skeleton
      // quedaba para siempre; mostramos el estado de error real (con reintento).
      setLoading(false);
      setLoadError(true);
      return;
    }
    let active = true;
    setLoading(true);
    setLoadError(false);
    getUser({ id: authUser.id })
      .then((res) => {
        if (!active) return;
        // El endpoint responde {data: user}; aceptamos también {data: {user}}
        // defensivamente por si el wrapper cambia.
        const payload = res?.data;
        const user = payload?._id ? payload : payload?.user;
        if (user && user._id) {
          setUserData(user);
        } else {
          showToast(res?.error?.message || t("Error al cargar los datos del usuario"), false);
          setLoadError(true);
        }
      })
      .catch((err) => {
        console.error("Error fetching user data:", err);
        showToast(t("Error al obtener los datos del usuario"), false);
        if (active) setLoadError(true);
      })
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [authUser, authLoading, reloadKey]);

  const company =
    userData?.companyId ||
    userData?.company ||
    authUser?.company ||
    authUser?.logistCompany;

  // Copia local del branding para reflejar la subida sin volver a pedir
  // `GET users/:id`: el documento de empresa llega poblado dentro del usuario,
  // así que refrescarlo entero solo para ver el logo nuevo es desproporcionado.
  const [logoOverride, setLogoOverride] = useState(null);
  const logoActual = logoOverride ?? company?.logo;
  const companyId = company?._id;

  // Solo los comercios con flota propia gestionan la config de entrega fallida
  // (los demás comercios no tienen repartidores propios).
  const hasOwnFleet =
    company?.hasOwnFleet === true || authUser?.company?.hasOwnFleet === true;

  const header = (
    <PageHeader
      icon={<Building2 className="w-6 h-6 text-fg-muted" />}
      title={t("Configuración de empresa")}
      subtitle={t("Información de tu empresa. Si necesitás corregir algo, contactá a tu administrador.")}
    />
  );

  if (loading) {
    // Espeja el layout real: PageHeader, hero de identidad y la grilla 2/3 +
    // 1/3 (Operación y Direcciones / Logo y Contactos). Un skeleton con otra
    // forma provoca salto de layout al cargar.
    const block = "rounded-lg border border-border bg-bg p-[1.25rem] space-y-4";
    const blockHead = (w) => (
      <div className="flex items-center gap-3">
        <Skeleton className="w-9 h-9 rounded-lg" />
        <Skeleton className={`h-4 ${w}`} />
      </div>
    );
    return (
      <div className="w-full min-w-0 p-4 md:p-6 space-y-5" role="status" aria-live="polite">
        <span className="sr-only">{t("Cargando…")}</span>
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <Skeleton className="h-6 w-6 rounded" />
            <Skeleton className="h-7 w-64 max-w-full" />
          </div>
          <Skeleton className="h-4 w-96 max-w-full" />
        </div>
        <div className="rounded-xl border border-border bg-bg p-[1.25rem] flex items-center gap-4">
          <Skeleton className="w-16 h-16 rounded-lg shrink-0" />
          <div className="flex-1 min-w-0 space-y-2">
            <Skeleton className="h-6 w-56 max-w-full" />
            <Skeleton className="h-4 w-72 max-w-full" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-[1rem] items-start">
          <div className="lg:col-span-2 space-y-4">
            <div className={block}>
              {blockHead("w-32")}
              <Skeleton className="h-4 w-40 max-w-full" />
              <div className="flex flex-wrap gap-1.5">
                {Array.from({ length: 7 }).map((_, i) => (
                  <Skeleton key={i} className="h-7 w-11 rounded-md" />
                ))}
              </div>
            </div>
            <div className={block}>
              {blockHead("w-44")}
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i} className="space-y-1.5">
                  <Skeleton className="h-4 w-80 max-w-full" />
                  <Skeleton className="h-3 w-28" />
                </div>
              ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className={block}>
              {blockHead("w-20")}
              <Skeleton className="h-32 w-full rounded-lg" />
            </div>
            <div className={block}>
              {blockHead("w-24")}
              <Skeleton className="h-4 w-40 max-w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loadError) {
    // Estado de error real con reintento: antes solo había un toast y la vista
    // renderizaba desde un fallback posiblemente stale (con hasOwnFleet
    // incognoscible, la sección de entrega fallida desaparecía en silencio).
    return (
      <div className="w-full min-w-0 p-4 md:p-6 space-y-5">
        {header}
        <div className="rounded-lg border border-border bg-bg-muted" role="alert">
          <EmptyState
            icon={<AlertTriangle />}
            title={t("No pudimos cargar los datos de tu empresa")}
            description={t("Revisá tu conexión y volvé a intentarlo.")}
            action={
              <Button
                variant="outline"
                iconLeft={<RefreshCw size={16} aria-hidden />}
                onClick={() => setReloadKey((k) => k + 1)}
              >
                {t("Reintentar")}
              </Button>
            }
          />
        </div>
      </div>
    );
  }

  // El logo solo tiene sentido si sabemos a qué empresa pertenece. Los roles
  // internos que entran acá por URL no tienen companyId y ven la ficha vacía;
  // tampoco corresponde ofrecerles subir un logo.
  const logoSection = companyId ? (
    <SectionCard
      tone="bg"
      // p-[1.25rem] y no p-5: Bootstrap pisa .p-5 con 3rem !important.
      className="p-[1.25rem]"
      icon={<ImageIcon className="w-4 h-4" aria-hidden />}
      title={t("Logo de tu empresa")}
      subtitle={t("Se guarda en dos formatos: la imagen para pantalla y una versión en blanco y negro para etiquetas térmicas.")}
    >
      <LogoUploader
        variant="compact"
        currentLogo={logoActual}
        onUpload={({ logoFile, zplBlob }) => uploadCompanyLogo(companyId, { logoFile, zplBlob })}
        onUploaded={(data) => setLogoOverride(data?.logo ?? logoActual)}
      />
    </SectionCard>
  ) : null;

  return (
    <div className="w-full min-w-0 p-4 md:p-6 space-y-5">
      {header}

      <CompanyInfoCard company={logoOverride ? { ...company, logo: logoOverride } : company} aside={logoSection} />

      {hasOwnFleet && (
        // DeliveryFailedFormConfig trae sus propias SectionCards: acá va solo
        // el encabezado del grupo, sin envolverlo en otra tarjeta.
        <section className="pt-2 space-y-3" aria-labelledby="company-settings-delivery-failed">
          <div>
            <h2 id="company-settings-delivery-failed" className="text-lg font-semibold text-fg">
              {t("Entrega fallida")}
            </h2>
            <p className="text-sm text-fg-muted mt-0.5">
              {t("Datos que tus repartidores deben registrar cuando una entrega falla, según el motivo.")}
            </p>
          </div>
          <DeliveryFailedFormConfig />
        </section>
      )}
    </div>
  );
}
